// D14 — client helpers for thermostat preset "apply" results. Mirrors
// PresetApplyResult in server/services/thermostat-preset-applier.ts.
import type { ThermostatPreset } from "@shared/schema";

export type PresetDeviceApplyStatus = "applied" | "unchanged" | "skipped" | "failed" | "pending";
export type ThermostatControlPolicyStatus = "pending" | "confirmed" | "failed" | "unsupported";
export interface ThermostatControlPolicyResult {
  status: ThermostatControlPolicyStatus;
  message: string;
}
export interface PresetDeviceApplyResult {
  deviceId: string;
  status: PresetDeviceApplyStatus;
  message: string | null;
  /** Provider command policy is distinct from the temperature application result. */
  controlPolicy?: ThermostatControlPolicyResult;
}
export interface PresetApplyResponse {
  presetId: number;
  season: "summer" | "winter" | null;
  appliedAt: string;
  results: PresetDeviceApplyResult[];
  summary: Record<PresetDeviceApplyStatus, number>;
}
// The last-apply columns are recorded on the preset row by the server.
export type PresetRow = ThermostatPreset & {
  lastAppliedAt?: string | Date | null;
  lastAppliedSeason?: string | null;
  lastApplyResults?: unknown;
};

export function readStoredApply(preset: PresetRow): PresetApplyResponse | null {
  const stored = preset.lastApplyResults as { results?: unknown; summary?: unknown } | null | undefined;
  if (!stored || !Array.isArray(stored.results) || !preset.lastAppliedAt) return null;
  const results = (stored.results as PresetDeviceApplyResult[]).filter((r) => r && typeof r.deviceId === "string");
  const summary: Record<PresetDeviceApplyStatus, number> = { applied: 0, unchanged: 0, skipped: 0, failed: 0, pending: 0 };
  for (const r of results) if (r.status in summary) summary[r.status] += 1;
  return {
    presetId: preset.id,
    season: preset.lastAppliedSeason === "summer" || preset.lastAppliedSeason === "winter" ? preset.lastAppliedSeason : null,
    appliedAt: new Date(preset.lastAppliedAt).toISOString(),
    results,
    summary,
  };
}

export function describeApplyResult(r: PresetApplyResponse): { text: string; tone: "ok" | "warn" | "error" } {
  const total = r.results.length;
  if (total === 0) return { text: "No thermostats linked", tone: "warn" };
  const ok = r.summary.applied + r.summary.unchanged;
  const seasonLabel = r.season ? ` (${r.season})` : "";
  if (r.summary.failed > 0) return { text: `Applied to ${ok} of ${total}${seasonLabel}; ${r.summary.failed} failed`, tone: "error" };
  if (ok === total) return { text: `Applied to ${ok} of ${total}${seasonLabel}`, tone: "ok" };
  return { text: `Applied to ${ok} of ${total}${seasonLabel}`, tone: "warn" };
}
